import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  PrinterIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { chemicalInventoryApi } from "../../../../../api/chemical-inventory";
import {
  getHseSafetyInspections,
  deleteHseSafetyInspection,
  completeHseSafetyInspection,
  closeHseSafetyInspection,
} from "../../../../../api/hse/safetyInspections";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../../components/utils/ResourceEmpty";
import DashboardHeader from "../../../../../components/dashboard/dashboardHeader";
import { PMDialog } from "../../../../../components/utils/PMDialog";
import { HseSafetyInspectionForm } from "../../../../../components/dashboard/hse/hseSafetyInspectionForm";

/**
 * HSE/KS Protective Equipment List - Vernerunder (ISO 45001 kap. 8.1.2)
 * Shows all protective equipment from chemical inventory and safety inspections
 */
export const HseKsProtectiveEquipmentList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [equipmentList, setEquipmentList] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    start_date: "",
    end_date: "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    current_count: 0,
    total_pages: 0,
  });

  const [activeTab, setActiveTab] = useState(() => {
    const path = location.pathname;
    if (path.includes("/matrix")) return "matrix";
    if (path.includes("/sja")) return "sja";
    if (path.includes("/climate")) return "climate";
    if (path.includes("/chemical-inventory")) return "chemical-inventory";
    if (path.includes("/protective-equipment")) return "protective-equipment";
    return "register";
  });

  const tabs = [
    { id: "register", label: "Risikoregister", path: "/dashboard/hse-ks/risk/register" },
    { id: "matrix", label: "Risikomatrise", path: "/dashboard/hse-ks/risk/matrix" },
    { id: "sja", label: "SJA", path: "/dashboard/hse-ks/risk/sja" },
    { id: "climate", label: "Klimarisiko", path: "/dashboard/hse-ks/risk/climate" },
    { id: "chemical-inventory", label: "Stoffkartotek", path: "/dashboard/hse-ks/risk/chemical-inventory" },
    { id: "protective-equipment", label: "Vernerunder", path: "/dashboard/hse-ks/risk/protective-equipment" },
  ];

  useEffect(() => {
    fetchEquipment();
    fetchInspections();
  }, [searchQuery, filters, pagination.page, pagination.per_page]);

  const fetchEquipment = async () => {
    try {
      const response = await chemicalInventoryApi.getChemicalInventories({
        search: searchQuery,
        per_page: 100,
      });
      
      // Extract protective equipment from all chemicals
      const allEquipment = [];
      if (response.data) {
        response.data.forEach((chemical) => {
          if (chemical.protective_equipment && Array.isArray(chemical.protective_equipment)) {
            chemical.protective_equipment.forEach((equipment) => {
              // Avoid duplicates by checking if equipment already exists
              const exists = allEquipment.find(
                (eq) => eq.name === equipment.name && eq.type === equipment.type
              );
              if (!exists) {
                allEquipment.push({
                  ...equipment,
                  chemical_id: chemical.id,
                  chemical_name: chemical.name,
                  cas_number: chemical.cas_number,
                });
              }
            });
          }
        });
      }
      
      setEquipmentList(allEquipment);
    } catch (err) {
      console.error("Error fetching protective equipment:", err);
    }
  };

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        page: pagination.page,
        per_page: pagination.per_page,
      };
      const response = await getHseSafetyInspections(params);
      setInspections(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta?.total || 0,
        current_count: response.data.meta?.current_count || 0,
        total_pages: response.data.meta?.total_pages || 0,
      }));
    } catch (err) {
      console.error("Error fetching inspections:", err);
      setError(true);
      toast.error(t("Kunne ikke laste vernerunder"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = (inspection) => {
    setInspections((prev) => {
      const existingIndex = prev.findIndex((i) => i.id === inspection.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = inspection;
        return updated;
      } else {
        return [inspection, ...prev];
      }
    });
    setShowForm(false);
    setSelectedInspection(null);
    fetchInspections();
  };

  const handleEdit = (inspection) => {
    setSelectedInspection(inspection);
    setShowForm(true);
  };

  const handleDelete = async (inspection) => {
    if (window.confirm(t("Er du sikker på at du vil slette denne vernerunden?"))) {
      try {
        await deleteHseSafetyInspection(inspection.id);
        toast.success(t("Vernerunde slettet"));
        fetchInspections();
      } catch (err) {
        toast.error(t("Feil ved sletting av vernerunde"));
      }
    }
  };

  const handleComplete = async (inspection) => {
    try {
      await completeHseSafetyInspection(inspection.id);
      toast.success(t("Vernerunde fullført"));
      fetchInspections();
    } catch (err) {
      toast.error(t("Feil ved fullføring av vernerunde"));
    }
  };

  const handleClose = async (inspection) => {
    try {
      await closeHseSafetyInspection(inspection.id);
      toast.success(t("Vernerunde lukket"));
      fetchInspections();
    } catch (err) {
      toast.error(t("Feil ved lukking av vernerunde"));
    }
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste vernerunder")]} />;
  }

  return (
    <>
      <DashboardHeader
        title="Vernerunder"
        subtitle="Oversikt over vernerunder og personlig verneutstyr (ISO 45001 - kap. 8.1.2)"
        mainButton={{
          label: t("Ny vernerunde"),
          icon: PlusIcon,
          action: () => {
            setSelectedInspection(null);
            setShowForm(true);
          },
        }}
      >
      <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
          {/* Navigation Tabs */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="border-b border-gray-200 px-4 py-3 sm:px-6">
              <nav className="flex space-x-4 overflow-x-auto" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      navigate(tab.path);
                    }}
                    className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                      activeTab === tab.id
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>
          {/* Search */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t("Søk i vernerunder...")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="bg-white shadow sm:rounded-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Status")}
                  </label>
                  <select
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">{t("Alle statuser")}</option>
                    <option value="draft">{t("Kladd")}</option>
                    <option value="in_progress">{t("Under gjennomføring")}</option>
                    <option value="completed">{t("Gjennomført")}</option>
                    <option value="closed">{t("Lukket")}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Dato fra")}
                  </label>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        start_date: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t("Dato til")}
                  </label>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        end_date: e.target.value,
                      }))
                    }
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Safety Inspections List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{t("Vernerunder")}</h3>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                {t("Filtre")}
              </button>
            </div>
            {loading ? (
              <div className="p-6">
                <ResourceLoading />
              </div>
            ) : !inspections || inspections.length === 0 ? (
              <div className="px-4 py-5 sm:p-6">
                <ResourceEmpty message={t("Ingen vernerunder funnet")} />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Tittel")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Dato")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Lokasjon")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Status")}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t("Handlinger")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {inspections.map((inspection) => (
                      <tr key={inspection.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {inspection.title}
                          </div>
                          <div className="text-sm text-gray-500">
                            {inspection.description}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(inspection.inspection_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {inspection.location}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              inspection.status === "draft"
                                ? "bg-gray-100 text-gray-800"
                                : inspection.status === "in_progress"
                                ? "bg-blue-100 text-blue-800"
                                : inspection.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : inspection.status === "closed"
                                ? "bg-gray-100 text-gray-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {inspection.status_display}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            {inspection.can_be_edited && (
                              <button
                                onClick={() => handleEdit(inspection)}
                                className="text-blue-600 hover:text-blue-900"
                                title={t("Rediger")}
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {inspection.can_be_completed && (
                              <button
                                onClick={() => handleComplete(inspection)}
                                className="text-green-600 hover:text-green-900"
                                title={t("Fullfør")}
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                            )}
                            {inspection.can_be_closed && (
                              <button
                                onClick={() => handleClose(inspection)}
                                className="text-orange-600 hover:text-orange-800"
                                title={t("Lukk")}
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                            {inspection.can_be_edited && (
                              <button
                                onClick={() => handleDelete(inspection)}
                                className="text-red-600 hover:text-red-800"
                                title={t("Slett")}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Protective Equipment List */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">{t("Personlig verneutstyr")}</h3>
            </div>
            {!equipmentList || equipmentList.length === 0 ? (
              <div className="px-4 py-5 sm:p-6">
                <ResourceEmpty message={t("Ingen verneutstyr funnet")} />
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {equipmentList.map((equipment, index) => (
                  <li key={`${equipment.chemical_id}-${index}`} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          <ShieldCheckIcon className="h-5 w-5 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {equipment.name || equipment.type || t("Ukjent utstyr")}
                            </p>
                          </div>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <span>{equipment.type && `${t("Type")}: ${equipment.type}`}</span>
                            <span>{equipment.chemical_name && `${t("Kjemikalium")}: ${equipment.chemical_name}`}</span>
                          </div>
                          {equipment.description && (
                            <p className="mt-1 text-sm text-gray-500">{equipment.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/dashboard/hse-ks/risk/chemical-inventory/${equipment.chemical_id}`}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          {t("Vis kjemikalium")}
                        </Link>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      </DashboardHeader>

      {/* Form Dialog */}
      {showForm && (
        <PMDialog
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setSelectedInspection(null);
          }}
          title={
            selectedInspection ? t("Rediger vernerunde") : t("Ny vernerunde")
          }
          size="7xl"
        >
          <HseSafetyInspectionForm
            inspection={selectedInspection}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false);
              setSelectedInspection(null);
            }}
          />
        </PMDialog>
      )}
    </>
  );
};

